/**
 * Best sellers tabs – tabbed product grid / carousel.
 * Quick add is wired into the theme cart (cart drawer + cart bubble) through
 * the standard CartLinesUpdateEvent used by product-form.js.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const WISHLIST_KEY = 'bs-wishlist';

function readWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch (e) {
    return [];
  }
}

function writeWishlist(list) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch (e) {
    /* storage unavailable */
  }
}

class BestSellersTabs extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.tabs = [...this.querySelectorAll('[data-bs-tab]')];
    this.panels = [...this.querySelectorAll('[data-bs-panel]')];
    this.viewAll = this.querySelector('[data-bs-view-all]');
    this.toast = this.querySelector('[data-bs-toast]');
    this.headerPrev = this.querySelector('.bs__header-arrows [data-bs-prev]');
    this.headerNext = this.querySelector('.bs__header-arrows [data-bs-next]');

    // Tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.bsTab, true), { signal });
      tab.addEventListener('keydown', (e) => this.onTabKey(e), { signal });
    });

    // Per-panel sliders
    this.panels.forEach((panel) => this.setupPanel(panel, signal));

    // Header arrows control the active panel
    this.headerPrev?.addEventListener('click', () => this.scrollActive(-1), { signal });
    this.headerNext?.addEventListener('click', () => this.scrollActive(1), { signal });

    // Card interactions (delegated)
    this.addEventListener('click', (e) => this.onClick(e), { signal });
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') this.closeAllVariants();
      },
      { signal }
    );
    document.addEventListener(
      'click',
      (e) => {
        if (!this.contains(e.target)) this.closeAllVariants();
      },
      { signal }
    );

    this.syncWishlist();

    // Measure media height for side arrows
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this);

    // Autoplay
    this.startAutoplay();
    this.addEventListener('mouseenter', () => this.stopAutoplay(), { signal });
    this.addEventListener('mouseleave', () => this.startAutoplay(), { signal });
    this.addEventListener('focusin', () => this.stopAutoplay(), { signal });

    // Theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (e) => {
          if (!this.contains(e.target)) return;
          const id = e.target.dataset.bsTab;
          if (id) this.activate(id, false);
        },
        { signal }
      );
      document.addEventListener(
        'shopify:section:select',
        (e) => {
          if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.stopAutoplay();
        },
        { signal }
      );
    }

    requestAnimationFrame(() => this.refresh());
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    this.stopAutoplay();
  }

  /* ---------------- Tabs ---------------- */

  get activePanel() {
    return this.panels.find((p) => p.classList.contains('is-active')) || this.panels[0];
  }

  activate(id, focus) {
    const tab = this.tabs.find((t) => t.dataset.bsTab === id);
    const panel = this.panels.find((p) => p.dataset.bsPanel === id);
    if (!panel) return;

    this.tabs.forEach((t) => {
      const on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });

    this.panels.forEach((p) => {
      const on = p === panel;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
      if (on) {
        p.classList.remove('is-entering');
        void p.offsetWidth;
        p.classList.add('is-entering');
      }
    });

    const track = panel.querySelector('[data-bs-track]');
    if (track) track.scrollLeft = 0;

    // View all link always points to the active tab's collection
    if (this.viewAll && tab?.dataset.bsUrl) {
      this.viewAll.href = tab.dataset.bsUrl;
    }

    if (focus && tab) tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

    this.closeAllVariants();
    requestAnimationFrame(() => this.refresh());
  }

  onTabKey(e) {
    const i = this.tabs.indexOf(e.currentTarget);
    let next = null;
    if (e.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
    if (e.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
    if (e.key === 'Home') next = this.tabs[0];
    if (e.key === 'End') next = this.tabs[this.tabs.length - 1];
    if (next) {
      e.preventDefault();
      next.focus();
      this.activate(next.dataset.bsTab, true);
    }
  }

  /* ---------------- Carousel ---------------- */

  setupPanel(panel, signal) {
    const track = panel.querySelector('[data-bs-track]');
    if (!track) return;

    panel.querySelector('.bs-slider [data-bs-prev]')?.addEventListener('click', () => this.scrollTrack(track, -1), {
      signal,
    });
    panel.querySelector('.bs-slider [data-bs-next]')?.addEventListener('click', () => this.scrollTrack(track, 1), {
      signal,
    });

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.updatePanel(panel);
          ticking = false;
        });
      },
      { passive: true, signal }
    );
  }

  isScrollable(track) {
    const style = getComputedStyle(track);
    return style.overflowX === 'auto' || style.overflowX === 'scroll';
  }

  pageSize(track) {
    const item = track.querySelector('.bs-track__item');
    if (!item) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const perView = Math.max(1, Math.round((track.clientWidth + gap) / (item.offsetWidth + gap)));
    return perView * (item.offsetWidth + gap);
  }

  scrollTrack(track, dir) {
    const max = track.scrollWidth - track.clientWidth;
    let target = track.scrollLeft + dir * this.pageSize(track);
    if (dir > 0 && track.scrollLeft >= max - 2) target = 0; // loop to start
    if (dir < 0 && track.scrollLeft <= 2) target = max; // loop to end
    track.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
  }

  scrollActive(dir) {
    const track = this.activePanel?.querySelector('[data-bs-track]');
    if (track) this.scrollTrack(track, dir);
  }

  updatePanel(panel) {
    const track = panel.querySelector('[data-bs-track]');
    const slider = panel.querySelector('.bs-slider');
    if (!track || !slider) return;

    const scrollable = this.isScrollable(track) && track.scrollWidth > track.clientWidth + 4;
    slider.classList.toggle('is-static', !scrollable);

    const max = Math.max(1, track.scrollWidth - track.clientWidth);
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= max - 2;

    // Side arrows (disabled at the edges)
    const prev = slider.querySelector('[data-bs-prev]');
    const next = slider.querySelector('[data-bs-next]');
    if (prev) prev.disabled = !scrollable || atStart;
    if (next) next.disabled = !scrollable || atEnd;

    // Header arrows mirror the active panel
    if (panel === this.activePanel) {
      if (this.headerPrev) this.headerPrev.disabled = !scrollable || atStart;
      if (this.headerNext) this.headerNext.disabled = !scrollable || atEnd;
    }

    // Progress bar
    const bar = panel.querySelector('[data-bs-progress]');
    if (bar) {
      const visible = Math.min(1, track.clientWidth / track.scrollWidth);
      const ratio = scrollable ? track.scrollLeft / max : 0;
      bar.style.setProperty('--bs-progress-w', `${visible * 100}%`);
      bar.style.setProperty('--bs-progress-x', `${(ratio * (1 - visible) * 100) / visible}%`);
    }

    // Dots
    const dots = panel.querySelector('[data-bs-dots]');
    if (dots) {
      const page = this.pageSize(track) || 1;
      const count = scrollable ? Math.ceil((track.scrollWidth - 4) / page) : 0;
      if (dots.childElementCount !== count) {
        dots.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'bs-dot';
          b.setAttribute('aria-label', `Go to slide ${i + 1}`);
          b.addEventListener('click', () => track.scrollTo({ left: i * page, behavior: 'smooth' }));
          dots.appendChild(b);
        }
      }
      const current = atEnd ? count - 1 : Math.round(track.scrollLeft / page);
      [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === current));
    }
  }

  refresh() {
    const media = this.activePanel?.querySelector('.bs-card__media');
    if (media) this.style.setProperty('--bs-media-h', `${media.offsetHeight}px`);
    this.panels.forEach((p) => {
      if (!p.hidden) this.updatePanel(p);
    });
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.dataset.designMode === 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    const speed = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.timer = setInterval(() => {
      const track = this.activePanel?.querySelector('[data-bs-track]');
      if (track && this.isScrollable(track)) this.scrollTrack(track, 1);
    }, speed);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /* ---------------- Card interactions ---------------- */

  onClick(e) {
    const target = e.target;

    const addBtn = target.closest('[data-bs-add]');
    if (addBtn && this.contains(addBtn)) {
      e.preventDefault();
      this.addToCart(addBtn);
      return;
    }

    const openBtn = target.closest('[data-bs-open-variants]');
    if (openBtn) {
      e.preventDefault();
      const panel = openBtn.parentElement.querySelector('[data-bs-variants]');
      const isOpen = panel && !panel.hidden;
      this.closeAllVariants();
      if (panel && !isOpen) {
        panel.hidden = false;
        openBtn.setAttribute('aria-expanded', 'true');
        panel.querySelector('button:not([disabled])[data-bs-add]')?.focus({ preventScroll: true });
      }
      return;
    }

    const closeBtn = target.closest('[data-bs-close-variants]');
    if (closeBtn) {
      e.preventDefault();
      this.closeAllVariants(true);
      return;
    }

    const wish = target.closest('[data-bs-wishlist]');
    if (wish) {
      e.preventDefault();
      this.toggleWishlist(wish);
      return;
    }

    const swatch = target.closest('.bs-swatch');
    if (swatch) {
      e.preventDefault();
      const card = swatch.closest('.bs-card');
      const img = card?.querySelector('.bs-card__img--primary');
      card?.querySelectorAll('.bs-swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
      if (img && swatch.dataset.bsSwatchImage) {
        img.removeAttribute('srcset');
        img.src = swatch.dataset.bsSwatchImage;
      }
    }
  }

  closeAllVariants(restoreFocus = false) {
    this.querySelectorAll('[data-bs-variants]:not([hidden])').forEach((panel) => {
      panel.hidden = true;
      const btn = panel.parentElement.querySelector('[data-bs-open-variants]');
      btn?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) btn?.focus({ preventScroll: true });
    });
  }

  syncWishlist() {
    const list = readWishlist();
    this.querySelectorAll('[data-bs-wishlist]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(list.includes(btn.dataset.bsWishlist)));
    });
  }

  toggleWishlist(btn) {
    const handle = btn.dataset.bsWishlist;
    let list = readWishlist();
    const on = !list.includes(handle);
    list = on ? [...list, handle] : list.filter((h) => h !== handle);
    writeWishlist(list);
    // Update every card for this product (it may appear in several tabs)
    this.querySelectorAll(`[data-bs-wishlist="${CSS.escape(handle)}"]`).forEach((b) =>
      b.setAttribute('aria-pressed', String(on))
    );
    this.showToast(on ? 'Added to wishlist' : 'Removed from wishlist');
  }

  showToast(message, isError = false) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.classList.toggle('is-error', isError);
    this.toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toast.hidden = true), 2400);
  }

  async refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      return cartItems.fetchCartData();
    }
    const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  async addToCart(button) {
    const variantId = button.dataset.bsAdd;
    if (!variantId || button.classList.contains('is-loading')) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await this.refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'best-sellers-tabs', itemCount: 1 },
        });
        this.showToast(data.description || data.message || 'Could not add to cart', true);
        return;
      }

      const cart = await this.refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'best-sellers-tabs',
          sourceId: this.dataset.sectionId,
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });

      this.closeAllVariants();

      // Cart drawer not set to auto-open: show a small confirmation
      if (!document.querySelector('cart-drawer-component[auto-open]')) {
        this.showToast(window.Theme?.translations?.added || 'Added to cart');
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.showToast('Network error, please try again', true);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('best-sellers-tabs')) {
  customElements.define('best-sellers-tabs', BestSellersTabs);
}
